(() => {
  if (customElements.get('downwool-product-explainer')) return;
  class DownwoolExplainer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.toggle = this.querySelector('[data-downwool-toggle]');
      this.slide = this.closest('.product-main-slide');
      this.starts = [0, 2.5, 5];
      this.enabled = !matchMedia('(prefers-reduced-motion: reduce)').matches && !navigator.connection?.saveData && matchMedia('(min-width: 769px)').matches;
      this.visible = false;
      this.phase = 0;
      this.hold = null;
      this.failed = false;
      this.toggle.addEventListener('click', () => {
        this.enabled = !this.enabled;
        this.sync();
      });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.seek(index));
        tab.addEventListener('keydown', event => {
          const destinations = { ArrowRight: (index + 1) % 3, ArrowLeft: (index + 2) % 3, Home: 0, End: 2 };
          if (!(event.key in destinations)) return;
          event.preventDefault();
          this.seek(destinations[event.key]);
          this.tabs[destinations[event.key]].focus();
        });
      });
      this.video.addEventListener('timeupdate', () => this.tick());
      this.video.addEventListener('loadedmetadata', () => {
        if (this.pendingSeek !== undefined) {
          this.video.currentTime = this.pendingSeek;
          delete this.pendingSeek;
        }
      });
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.enabled = false;
        this.sync();
        this.toggle.hidden = true;
      });
      this.onVisibility = () => this.sync();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.sync();
      }, { threshold: .15 });
      this.observer.observe(this);
      this.selectionObserver = new MutationObserver(() => this.sync());
      this.selectionObserver.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
      this.render(0, 0);
      this.sync();
    }
    active() {
      return this.visible && this.slide.classList.contains('is-selected') && !document.hidden;
    }
    load() {
      if (!this.video.getAttribute('src') && !this.failed) {
        this.video.src = this.video.dataset.src;
        this.video.load();
      }
    }
    sync() {
      const play = this.enabled && this.active() && !this.failed;
      this.toggle.textContent = play ? 'Ⅱ' : '▶';
      this.toggle.setAttribute('aria-label', play ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel);
      if (!play) {
        clearTimeout(this.hold);
        this.hold = null;
        this.video.pause();
        return;
      }
      this.load();
      if (this.hold) return;
      this.video.play().catch(() => {
        this.enabled = false;
        this.sync();
      });
    }
    render(index, progress) {
      this.phase = index;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
      this.style.setProperty('--downwool-progress', progress);
    }
    seek(index) {
      this.enabled = false;
      this.sync();
      this.load();
      this.render(index, 0);
      if (this.video.readyState >= 1) this.video.currentTime = this.starts[index];
      else this.pendingSeek = this.starts[index];
    }
    tick() {
      const time = this.video.currentTime;
      const index = this.phase;
      const end = index < 2 ? this.starts[index + 1] : (this.video.duration || 7.333);
      this.render(index, Math.max(0, Math.min(1, (time - this.starts[index]) / (end - this.starts[index]))));
      if (!this.enabled || !this.active() || this.hold || time < end - .12) return;
      this.video.pause();
      this.video.currentTime = Math.max(this.starts[index], end - .04);
      this.render(index, 1);
      this.hold = setTimeout(() => {
        this.hold = null;
        if (!this.enabled || !this.active()) return;
        const next = (index + 1) % 3;
        this.video.currentTime = this.starts[next];
        this.render(next, 0);
        this.sync();
      }, 1800);
    }
    disconnectedCallback() {
      clearTimeout(this.hold);
      this.video?.pause();
      this.observer?.disconnect();
      this.selectionObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }
  customElements.define('downwool-product-explainer', DownwoolExplainer);
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-downwool-open]');
    if (!link) return;
    const section = link.closest('.product-section');
    const thumb = section?.querySelector('[data-product-thumb][data-id="downwool-explainer"]');
    if (!thumb) return;
    event.preventDefault();
    thumb.click();
    section.querySelector('[data-product-photos]').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    section.querySelector('downwool-product-explainer [role="tab"][aria-selected="true"]')?.focus({ preventScroll: true });
  });
})();
